import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useCallback, useEffect, useState } from "react";
import { dateLocale } from "@/lib/dateTime";
import { Check, GitMerge, Loader2, Play, Save, Trash2, Upload, Volume2, X } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { StatusPill } from "@/components/patterns";
import { CallPlayer } from "@/components/calling/CallPlayer";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorState } from "@/components/patterns/States";
import { cn } from "@/lib/utils";

type Rep = { id: string; name: string; enabled: boolean };
type Dup = { number: string; can_merge?: boolean; records: Array<{ type: string; id: string; label: string; owner_id: string | null; owner?: string | null }> };
type Sound = { id: string; name: string; size_bytes: number; uploaded_on: string };
const BUILTIN = [{ key: "builtin:chime", label: "Soft chime", sub: "Built in · 4 s", url: "/sounds/ring-chime.wav" }, { key: "builtin:bell", label: "Classic bell", sub: "Built in · 3 s", url: "/sounds/ring-bell.wav" }, { key: "builtin:digital", label: "Digital ring", sub: "Built in · 2 s", url: "/sounds/ring-digital.wav" }];
type Settings = {
  connected: boolean;
  inbound_enabled?: boolean;
  ring_owner_sec?: number;
  ring_team_sec?: number;
  ring_overflow_sec?: number;
  overflow_users?: Array<{ id: string; name: string }>;
  voicemail_enabled?: boolean;
  after_hours_text?: string;
  off_text?: string;
  missed_text?: string;
  calling_hours?: { enabled: boolean; start: string; end: string; days: number[]; timezone: string };
  callback_sla_min?: number;
  recent_caller_days?: number;
  owner_no_answer_action?: "VOICEMAIL" | "TEAM";
  owner_busy_action?: "VOICEMAIL" | "TEAM";
  owner_away_action?: "VOICEMAIL" | "TEAM";
  wrap_up_sec?: number;
  allow_pause?: boolean;
  away_remind_min?: number;
  decline_alert_per_day?: number;
  escalate_head_min?: number;
  escalate_admin_min?: number;
  ringtone?: string;
  ringtone_volume?: number;
  desktop_notify?: boolean;
  sounds?: Sound[];
  caller_id?: string;
  answer_url?: string;
  app_ready?: boolean;
  reps_online?: number;
  duplicates?: Dup[];
};

const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function hoursSummary(h?: { enabled: boolean; start: string; end: string; days: number[]; timezone: string }) {
  if (!h || !h.enabled) return "No limit: calls go through at any time.";
  const d = [...h.days].sort();
  const contiguous = d.length > 2 && d.every((x, i) => i === 0 || x === d[i - 1]! + 1);
  const days = d.length === 7 ? "Every day" : contiguous ? `${DAY[d[0]!]}–${DAY[d[d.length - 1]!]}` : d.map((x) => DAY[x]).join(", ");
  return `${days}, ${h.start}–${h.end} (${h.timezone.replace("_", " ")})`;
}

const Card = ({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2.5 rounded-xl border border-border bg-card px-4 py-3.5">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

function Check_({ ok, title, detail }: { ok: boolean; title: string; detail: string }) {
  return (
    <div className="flex items-start gap-2.5 border-t border-border/60 py-2.5 first:border-t-0">
      <span className={cn("mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full", ok ? "bg-success-soft text-success" : "bg-warning-soft text-warning")}>
        {ok ? <Check className="size-3" strokeWidth={3} /> : <X className="size-3" strokeWidth={3} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">{title}</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
      </span>
    </div>
  );
}

/** Settings → Calling → Incoming (admin): who rings first when a customer calls the shared number */
export function IncomingCallsPanel({ onOpenHours, section = "incoming" }: { onOpenHours?: () => void; section?: "incoming" | "ringtone" }) {
  const { success, error } = useToast();
  const [data, setData] = useState<Settings | null>(null);
  const [problem, setProblem] = useState<unknown>(null);
  const [reps, setReps] = useState<Rep[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mergeDup, setMergeDup] = useState<Dup | null>(null);
  const [keepId, setKeepId] = useState<string>("");
  const [merging, setMerging] = useState(false);
  // The sound being previewed: its row opens a player (play/pause, scrubber, time) under it
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [f, setF] = useState({ inbound_enabled: false, ring_owner_sec: 20, ring_team_sec: 20, ring_overflow_sec: 20, overflow: [] as string[], voicemail_enabled: true, after_hours_text: "", off_text: "", missed_text: "", callback_sla_min: 30, recent_caller_days: 7, owner_no_answer_action: "VOICEMAIL" as "VOICEMAIL" | "TEAM", owner_busy_action: "VOICEMAIL" as "VOICEMAIL" | "TEAM", owner_away_action: "TEAM" as "VOICEMAIL" | "TEAM", wrap_up_sec: 30, allow_pause: true, away_remind_min: 30, decline_alert_per_day: 5, escalate_head_min: 15, escalate_admin_min: 120, ringtone: "builtin:chime", ringtone_volume: 70, desktop_notify: true });

  const fill = useCallback((s: Settings) => {
    setData(s);
    setF({
      inbound_enabled: !!s.inbound_enabled,
      ring_owner_sec: s.ring_owner_sec ?? 20,
      ring_team_sec: s.ring_team_sec ?? 20,
      ring_overflow_sec: s.ring_overflow_sec ?? 20,
      overflow: (s.overflow_users || []).map((u) => u.id),
      voicemail_enabled: s.voicemail_enabled ?? true,
      after_hours_text: s.after_hours_text || "",
      off_text: s.off_text || "",
      missed_text: s.missed_text || "",
      callback_sla_min: s.callback_sla_min ?? 30,
      owner_no_answer_action: s.owner_no_answer_action || "VOICEMAIL",
      owner_busy_action: s.owner_busy_action || "VOICEMAIL",
      owner_away_action: s.owner_away_action || "TEAM",
      wrap_up_sec: s.wrap_up_sec ?? 30,
      allow_pause: s.allow_pause ?? true,
      away_remind_min: s.away_remind_min ?? 30,
      decline_alert_per_day: s.decline_alert_per_day ?? 5,
      escalate_head_min: s.escalate_head_min ?? 15,
      escalate_admin_min: s.escalate_admin_min ?? 120,
      ringtone: s.ringtone || "builtin:chime",
      ringtone_volume: s.ringtone_volume ?? 70,
      desktop_notify: s.desktop_notify ?? true,
      recent_caller_days: s.recent_caller_days ?? 7,
    });
  }, []);

  const load = useCallback(async () => {
    try {
      fill(unwrapObject<Settings>(await api.get("/api/Call/Inbound/Settings")));
      setProblem(null);
    } catch (err) {
      setProblem(err);
    }
  }, [fill]);

  useEffect(() => {
    void load();
    api.get("/api/Call/Reps").then((r) => setReps(unwrapObject<Rep[]>(r) || [])).catch(() => setReps([]));
  }, [load]);

  const save = async (patch?: Partial<{ inbound_enabled: boolean }>) => {
    setSaving(true);
    try {
      const v = { ...f, ...patch };
      fill(unwrapObject<Settings>(await api.post("/api/Call/Inbound/Settings", {
        inbound_enabled: v.inbound_enabled, ring_owner_sec: v.ring_owner_sec, ring_team_sec: v.ring_team_sec, ring_overflow_sec: v.ring_overflow_sec,
        overflow_user_ids: v.overflow, voicemail_enabled: v.voicemail_enabled, after_hours_text: v.after_hours_text, off_text: v.off_text, missed_text: v.missed_text, callback_sla_min: v.callback_sla_min, recent_caller_days: v.recent_caller_days, owner_no_answer_action: v.owner_no_answer_action, owner_busy_action: v.owner_busy_action, owner_away_action: v.owner_away_action, wrap_up_sec: v.wrap_up_sec, allow_pause: v.allow_pause, away_remind_min: v.away_remind_min, decline_alert_per_day: v.decline_alert_per_day, escalate_head_min: v.escalate_head_min, escalate_admin_min: v.escalate_admin_min, ringtone: v.ringtone, ringtone_volume: v.ringtone_volume, desktop_notify: v.desktop_notify,
      })));
      success("Incoming calls saved", v.inbound_enabled ? "Calls to the shared number now ring your reps." : "Callers hear the closed message and can leave a voicemail.");
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  if (problem) return <ErrorState title="Could not load the incoming-call settings" error={problem} onRetry={load} />;
  if (!data) return <FormSkeleton />;
  if (!data.connected) {
    return (
      <div className="space-y-4">
        <Card title="Connect your calling account first">
          <p className="text-[13px] text-muted-foreground">Incoming calls use the same calling account as outgoing ones. Set it up in Settings → Calling.</p>
        </Card>
      </div>
    );
  }

  const num = (k: "ring_owner_sec" | "ring_team_sec" | "ring_overflow_sec" | "callback_sla_min" | "recent_caller_days" | "escalate_head_min" | "escalate_admin_min" | "wrap_up_sec" | "away_remind_min" | "decline_alert_per_day", min: number, max: number) => (
    <Input type="number" min={min} max={max} value={f[k]} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} className="w-24 text-right font-mono" />
  );
  const pick = (k: "owner_no_answer_action" | "owner_busy_action" | "owner_away_action") => (
    <SearchableSelect value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value as "VOICEMAIL" | "TEAM" })} aria-label={k} className="h-9 w-56 rounded-md border border-input bg-background px-2.5 text-sm">
      <option value="VOICEMAIL">Voicemail for the owner</option>
      <option value="TEAM">Their team, then overflow</option>
    </SearchableSelect>
  );
  const fixedNote = (t: string) => <span className="max-w-[14rem] text-right text-xs text-muted-foreground">{t}</span>;
  const row = (label: string, sub: string, control: React.ReactNode) => (
    <div className="flex items-center gap-3 border-t border-border/60 py-2.5 first:border-t-0">
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">{label}</span>
        <span className="block text-xs text-muted-foreground">{sub}</span>
      </span>
      {control}
    </div>
  );
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("name", file.name);
      await api.post("/api/Call/Inbound/Sounds", body);
      await load();
      success("Sound added", "Pick it in the list to use it.");
    } catch (err: any) {
      error("Couldn't add the sound", err.message || "Use an MP3 or WAV under 300 KB, up to 10 seconds.");
    } finally {
      setUploading(false);
    }
  };
  const removeSound = async (sound: Sound) => {
    try {
      await api.delete(`/api/Call/Inbound/Sounds/${sound.id}`);
      await load();
      success("Sound deleted", f.ringtone === `custom:${sound.id}` ? "Incoming calls ring with the soft chime again." : sound.name);
    } catch (err: any) {
      error("Couldn't delete it", err.message || "Please try again");
    }
  };
  const notDuplicate = async (d: Dup) => {
    try {
      fill(unwrapObject<Settings>(await api.post("/api/Call/Inbound/SharedNumber", { number: d.number })));
      success("Marked as shared", "It still rings both owners and leaves this list.");
    } catch (err: any) {
      error("Couldn't save", err.message || "Please try again");
    }
  };
  const merge = async () => {
    if (!mergeDup || !keepId) return;
    const drop = mergeDup.records.find((r) => r.id !== keepId);
    if (!drop) return;
    setMerging(true);
    try {
      await api.post("/api/Customer/MergeContacts", { keep_id: keepId, merge_id: drop.id });
      setMergeDup(null);
      await load();
      success("Records merged", "Everything moved to the contact you kept.");
    } catch (err: any) {
      error("Couldn't merge", err.message || "You may not have permission to merge contacts.");
    } finally {
      setMerging(false);
    }
  };
  const group = f.overflow.length;
  const eligible = reps.filter((r) => r.enabled);

  const ringtoneCard = (
      <Card title="Ringtone">
        <p className="text-xs text-muted-foreground">The sound every rep hears when a customer calls. Reps can switch it off or turn it down on their own device, but can't change it.</p>
        <div role="radiogroup" aria-label="Ringtone" className="divide-y rounded-lg border">
          {[...BUILTIN.map((b) => ({ key: b.key, label: b.label, sub: b.sub, url: b.url, custom: null as Sound | null })), ...(data.sounds || []).map((x) => ({ key: `custom:${x.id}`, label: x.name, sub: `Uploaded ${new Date(x.uploaded_on).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })} · ${Math.max(1, Math.round(x.size_bytes / 1024))} KB`, url: `/api/Call/Sound/${x.id}`, custom: x }))].map((o) => (
            <div key={o.key} className="px-3 py-2">
             <div className="flex items-center gap-2.5">
              <button type="button" role="radio" aria-checked={f.ringtone === o.key} onClick={() => setF({ ...f, ringtone: o.key })} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                <span className={cn("flex size-4 shrink-0 items-center justify-center rounded-full border-2", f.ringtone === o.key ? "border-primary" : "border-input")}>{f.ringtone === o.key && <span className="size-2 rounded-full bg-primary" />}</span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-semibold">{o.label}</span>
                  <span className="block truncate text-xs text-muted-foreground">{o.sub}</span>
                </span>
              </button>
              <Button type="button" variant="outline" size="icon" className="size-7" onClick={() => setPreviewing(previewing === o.key ? null : o.key)} aria-label={previewing === o.key ? `Close the player for ${o.label}` : `Play ${o.label}`} aria-expanded={previewing === o.key}>{previewing === o.key ? <X className="size-3" /> : <Play className="size-3" />}</Button>
              {o.custom && <Button type="button" variant="ghost" size="icon" className="size-7 text-destructive" onClick={() => removeSound(o.custom!)} aria-label={`Delete ${o.label}`}><Trash2 className="size-3.5" /></Button>}
             </div>
             {previewing === o.key && (
               <div className="mt-2 rounded-lg bg-panel px-3 py-2.5">
                 <CallPlayer src={o.url} autoPlay showSpeed={false} volume={f.ringtone_volume / 100} onError={() => { setPreviewing(null); error("Couldn't play the sound", "Your browser blocked it, or the file is missing."); }} />
               </div>
             )}
            </div>
          ))}
        </div>
        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border bg-panel px-3 py-2.5">
          {uploading ? <Loader2 className="size-5 animate-spin text-primary" /> : <Upload className="size-5 text-primary" />}
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-semibold">Add a sound</span>
            <span className="block text-xs text-muted-foreground">MP3 or WAV, up to 10 seconds and 300 KB. It loops while the phone rings.</span>
          </span>
          <input type="file" accept="audio/mpeg,audio/wav,.mp3,.wav" className="sr-only" disabled={uploading} onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
          <span className="rounded-md border border-input bg-background px-2.5 py-1 text-xs font-semibold">Choose file</span>
        </label>
        <div className="flex items-center gap-3 border-t border-border/60 pt-2.5">
          <Volume2 className="size-4 text-muted-foreground" />
          <input type="range" min={0} max={100} value={f.ringtone_volume} onChange={(e) => setF({ ...f, ringtone_volume: Number(e.target.value) })} aria-label="Starting volume" className="h-1 flex-1 cursor-pointer accent-primary" />
          <span className="w-10 text-right font-mono text-xs">{f.ringtone_volume}%</span>
        </div>
        {row("Desktop notification", "“Incoming call from …” when the CRM is in another tab", <Switch checked={f.desktop_notify} onCheckedChange={(v) => setF({ ...f, desktop_notify: v })} aria-label="Desktop notification" />)}
      </Card>
  );

  if (section === "ringtone") {
    return (
      <div className="space-y-4">
        <div className="max-w-2xl">{ringtoneCard}</div>
        <div className="flex max-w-2xl justify-end">
          <Button onClick={() => save()} disabled={saving}>
            {saving ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Save className="mr-1.5 size-4" />}
            Save changes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <span>
          <span className="block text-[13px] font-semibold">Incoming calls</span>
          <span className="block text-xs text-muted-foreground">{f.inbound_enabled ? "On: calls to the shared number ring your reps." : "Off: callers hear the message below and can leave a voicemail."}</span>
        </span>
        <Switch checked={f.inbound_enabled} disabled={saving} onCheckedChange={(v) => { setF({ ...f, inbound_enabled: v }); void save({ inbound_enabled: v }); }} aria-label="Incoming calls" />
      </div>
      <p className="text-[13px] text-muted-foreground">Decide who rings when a customer calls the shared number. A call nobody takes goes to voicemail and becomes a callback task. Nothing is ever rejected.</p>

      <div className="flex flex-wrap items-start gap-3.5">
        <div className="flex min-w-0 flex-[1_1_460px] flex-col gap-3.5">
          <Card title="Ready check" aside={<StatusPill tone={data.app_ready && eligible.length && group >= 1 ? "success" : "warning"}>{data.app_ready && eligible.length && group >= 1 ? "All good" : "Needs attention"}</StatusPill>}>
            <Check_ ok={!!data.app_ready} title="Calling account is connected and set up" detail={data.app_ready ? `The shared number +${data.caller_id} sends calls to the CRM.` : "Finish the calling setup in Settings → Calling."} />
            <Check_ ok={!!data.app_ready} title="Calls reach the CRM" detail={`On your calling provider's dashboard, the number's answer link should be ${data.answer_url}. Incoming calls use the same link as outgoing ones.`} />
            <Check_ ok={(data.reps_online || 0) > 0} title="Someone is online" detail={`${data.reps_online || 0} ${data.reps_online === 1 ? "person is" : "people are"} ready to take calls right now. Callers go to voicemail when nobody is.`} />
            <Check_ ok={group >= 2} title={`Overflow group has ${group} ${group === 1 ? "person" : "people"}`} detail={group >= 2 ? "Unknown callers and calls nobody else takes ring these people." : "Add at least 2 people so unknown callers are answered when someone is busy. With none, everyone with calling on rings."} />
          </Card>

          <Card title="Ring order">
            {row("1. The lead's owner", "Only if they're ready to take calls and not on a call", <span className="flex items-center gap-1.5">{num("ring_owner_sec", 5, 60)}<span className="text-xs text-muted-foreground">s</span></span>)}
            {row("2. The owner's team", "Their head and teammates ring together; first to answer wins", <span className="flex items-center gap-1.5">{num("ring_team_sec", 5, 60)}<span className="text-xs text-muted-foreground">s</span></span>)}
            {row("3. Overflow group", "Unknown callers start here", <span className="flex items-center gap-1.5">{num("ring_overflow_sec", 5, 60)}<span className="text-xs text-muted-foreground">s</span></span>)}
            {row("4. Voicemail", "A greeting plays and the caller leaves a message", <Switch checked={f.voicemail_enabled} onCheckedChange={(v) => setF({ ...f, voicemail_enabled: v })} aria-label="Voicemail" />)}
          </Card>

          <Card title="When the lead's owner can't take the call">
            <p className="text-xs text-muted-foreground">A customer's call belongs to their owner. It only moves to someone else when the owner can't be reached at all.</p>
            {row("Owner declines", "They pressed Decline", fixedNote("Voicemail for the owner"))}
            {row("Owner is available but doesn't answer", "After the ring time under Ring order", pick("owner_no_answer_action"))}
            {row("Owner is on another call or in wrap-up", "", pick("owner_busy_action"))}
            {row("Owner is away", "They paused incoming calls", pick("owner_away_action"))}
            {row("Owner is offline", "Not signed in, or the tab was closed", fixedNote("Team, then overflow, then voicemail"))}
          </Card>

          <Card title="After a call and while away">
            {row("Wrap-up after each call", "No new calls ring the rep while they save the outcome. 0 switches it off.", <span className="flex items-center gap-1.5">{num("wrap_up_sec", 0, 300)}<span className="text-xs text-muted-foreground">s</span></span>)}
            {row("Reps can pause incoming calls", "The Away switch in the call chip. Ends by itself at sign-out or the end of the day.", <Switch checked={f.allow_pause} onCheckedChange={(v) => setF({ ...f, allow_pause: v })} aria-label="Reps can pause incoming calls" />)}
            {row("Remind a rep who is away with no end time after", "A bell reminder with a way back. 0 switches it off.", <span className="flex items-center gap-1.5">{num("away_remind_min", 0, 1440)}<span className="text-xs text-muted-foreground">min</span></span>)}
            {row("Tell the head when a rep declines more than", "Customer calls per day. 0 switches it off.", <span className="flex items-center gap-1.5">{num("decline_alert_per_day", 0, 100)}<span className="text-xs text-muted-foreground">a day</span></span>)}
          </Card>

          <Card title="Who answers first">
            {row("Treat a customer as the same rep's if they were called within", "A customer calling back is almost always calling back the rep who called them.", <span className="flex items-center gap-1.5">{num("recent_caller_days", 0, 90)}<span className="text-xs text-muted-foreground">days</span></span>)}
          </Card>
        </div>

        <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-3.5">
          <Card title="Overflow group">
            <div className="flex flex-wrap gap-1.5">
              {eligible.length === 0 && <p className="text-xs text-muted-foreground">Switch calling on for reps in Settings → Calling first.</p>}
              {eligible.map((r) => {
                const on = f.overflow.includes(r.id);
                return (
                  <button key={r.id} type="button" onClick={() => setF({ ...f, overflow: on ? f.overflow.filter((x) => x !== r.id) : [...f.overflow, r.id] })} aria-pressed={on} className={cn("inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-xs font-medium", on ? "border-primary bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:bg-muted")}>
                    {on && <Check className="size-3" strokeWidth={3} />}
                    {r.name}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">Rings last, and first for callers we don't know.</p>
          </Card>

          <Card title="Calling hours" aside={onOpenHours && <button type="button" onClick={onOpenHours} className="text-xs font-semibold text-primary hover:underline">Change hours</button>}>
            <p className="text-[13px]">{hoursSummary(data.calling_hours)}</p>
            <p className="text-xs text-muted-foreground">The same hours apply to outgoing and incoming calls. Outside them, callers hear the closed message and can leave a voicemail.</p>
          </Card>

          <Card title="What callers hear">
            <div className="space-y-1">
              <Label htmlFor="ah" className="text-[12.5px] font-medium">Outside calling hours</Label>
              <Textarea id="ah" rows={3} value={f.after_hours_text} onChange={(e) => setF({ ...f, after_hours_text: e.target.value })} maxLength={400} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="offt" className="text-[12.5px] font-medium">When incoming calls are switched off</Label>
              <Textarea id="offt" rows={3} value={f.off_text} onChange={(e) => setF({ ...f, off_text: e.target.value })} maxLength={400} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="missedt" className="text-[12.5px] font-medium">When nobody can answer</Label>
              <Textarea id="missedt" rows={3} value={f.missed_text} onChange={(e) => setF({ ...f, missed_text: e.target.value })} maxLength={400} />
              <p className="text-xs text-muted-foreground">Played in calling hours when your reps are busy or offline, just before the beep. It should ask them to leave a message. With voicemail off, callers hear a short fixed line instead.</p>
            </div>
            {row("Callback due within", "Overdue callbacks show in red on the Calls page", <span className="flex items-center gap-1.5">{num("callback_sla_min", 5, 1440)}<span className="text-xs text-muted-foreground">min</span></span>)}
            {row("Tell the owner's head when overdue by", "A bell notification. 0 switches it off.", <span className="flex items-center gap-1.5">{num("escalate_head_min", 0, 1440)}<span className="text-xs text-muted-foreground">min</span></span>)}
            {row("Tell an admin when overdue by", "For calls nobody has taken in a long time. 0 switches it off.", <span className="flex items-center gap-1.5">{num("escalate_admin_min", 0, 10080)}<span className="text-xs text-muted-foreground">min</span></span>)}
          </Card>

          <Card title="Same number on 2 records" aside={<StatusPill tone={data.duplicates?.length ? "warning" : "success"}>{data.duplicates?.length ? `${data.duplicates.length} found` : "None"}</StatusPill>}>
            {(data.duplicates || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No number sits on records owned by different people.</p>
            ) : (
              (data.duplicates || []).map((d) => (
                <div key={d.number} className="space-y-1.5 border-t border-border/60 py-2 first:border-t-0">
                  <span className="block font-mono text-[13px] font-semibold">{d.number}</span>
                  {d.records.map((r) => (
                    <span key={r.id} className="block text-xs text-muted-foreground">
                      <a href={`/${r.type === "LEAD" ? "leads" : "contacts"}/${r.id}`} className="font-semibold text-primary hover:underline">{r.label || "(no name)"}</a> · {r.type.toLowerCase()}{r.owner ? ` · owner ${r.owner}` : ""}
                    </span>
                  ))}
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {d.can_merge ? (
                      <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => { setMergeDup(d); setKeepId(d.records[0]!.id); }}><GitMerge className="mr-1 size-3.5" />Review and merge</Button>
                    ) : (
                      <span className="self-center text-[11.5px] text-muted-foreground">Open each record and fix the number on one.</span>
                    )}
                    <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => notDuplicate(d)}>Not a duplicate</Button>
                  </div>
                </div>
              ))
            )}
            <p className="text-xs text-muted-foreground">An incoming call from one of these rings both owners. Merge or fix the duplicate so it always rings one, or mark it as really shared.</p>
          </Card>

          <Card title="If something goes wrong">
            <p className="text-[13px] text-muted-foreground">Switch incoming calls off above. Callers then hear the closed message and can leave a voicemail. Nothing is rejected and every call is still logged.</p>
          </Card>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => save()} disabled={saving}>
          {saving ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Save className="mr-1.5 size-4" />}
          Save changes
        </Button>
      </div>

      <Dialog open={!!mergeDup} onOpenChange={(o) => !o && setMergeDup(null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Merge these two contacts</DialogTitle>
            <DialogDescription>{mergeDup?.number} is on both. Pick the one to keep.</DialogDescription>
          </DialogHeader>
          <div role="radiogroup" aria-label="Contact to keep" className="grid gap-2 sm:grid-cols-2">
            {mergeDup?.records.map((r) => (
              <button key={r.id} type="button" role="radio" aria-checked={keepId === r.id} onClick={() => setKeepId(r.id)} className={cn("rounded-lg border p-3 text-left", keepId === r.id ? "border-primary bg-accent" : "border-border hover:bg-muted")}>
                <span className="block text-sm font-semibold">{r.label || "(no name)"}</span>
                <span className="block text-xs text-muted-foreground">{r.owner ? `Owner ${r.owner}` : "No owner"}</span>
              </button>
            ))}
          </div>
          <ul className="space-y-0.5 rounded-lg bg-panel px-3 py-2 text-[12.5px]">
            <li>Activities, calls, tasks and deals move to the contact you keep.</li>
            <li>The other contact is removed.</li>
            <li className="text-destructive">This can't be undone. It is written to the security log.</li>
          </ul>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMergeDup(null)}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={merge} disabled={merging || !keepId}>
              {merging && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              Merge contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
